import {
  AgencyListingCard,
  BrandListingCard,
  ListingCard,
  SchoolListingCard,
  TalentListingCard,
} from '../components';
import { enhancedServicesKeys } from '../config/configSearch';
import { UserType } from './enums';

// Card type mapping
export const CARD_COMPONENTS = {
  [UserType.TALENT]: TalentListingCard,
  [UserType.BRAND]: BrandListingCard,
  [UserType.SCHOOL]: SchoolListingCard,
  [UserType.AGENCY]: AgencyListingCard,
  // Default fallback
  default: ListingCard,
};

// Utility function to get listing category
export const getListingCategory = listing => {
  try {
    return listing?.attributes?.publicData?.listingCategory || null;
  } catch (error) {
    return null;
  }
};

// Utility function to get card component
export const getCardComponent = category => {
  if (!category) {
    return CARD_COMPONENTS.default;
  }
  return CARD_COMPONENTS[category] || CARD_COMPONENTS.default;
};

// Helper function to extract non-enhanced Talent Plus services
export const extractTalentPlusServices = (talentPlus = []) => {
  const result = {
    talentPlus: [],
  };

  for (const item of talentPlus) {
    if (!item?.selected) continue;

    result.talentPlus.push({
      type: item.type || 'Unknown Type',
      price: item.price || 'N/A',
    });
  }

  return result;
};

export const extractEnhancedServices = (talentPlus = []) => {
  const services = Object.fromEntries(enhancedServicesKeys.map(key => [key, []]));
  for (const item of talentPlus) {
    if (!item?.selected || !item?.service) continue;

    const normalized = item.service.toLowerCase().replace(/[\s-]/g, '');
    const matchedKey = enhancedServicesKeys.find(key => key.toLowerCase() === normalized);

    if (matchedKey) {
      services[matchedKey].push({
        type: item.type || 'Unknown Type',
        price: item.price || 'N/A',
      });
    }
  }

  return services;
};
// Helper function to format Signature Services
export const formatSignatureServices = publicData => {
  const services = {};

  // Autographs
  const autographs = publicData?.autographs || {};
  const {
    photoPrice,
    posterPrice,
    tradingCardPrice,
    showFanProvidedItemsPrice,
    customItems = [],
  } = autographs;

  const autographItems = [
    photoPrice && { type: 'Photo', price: photoPrice },
    posterPrice && { type: 'Poster', price: posterPrice },
    tradingCardPrice && { type: 'Trading Card', price: tradingCardPrice },
    showFanProvidedItemsPrice && { type: 'Fan Provided Items', price: showFanProvidedItemsPrice },
    ...customItems
      .filter(item => item?.price && item?.title)
      .map(item => ({ type: item.title, price: item.price })),
  ].filter(Boolean);

  if (autographItems.length) {
    services.autographs = autographItems;
  }

  // Shoutouts
  const shoutouts = publicData?.shoutouts || [];
  const shoutoutItems = shoutouts
    .filter(s => s?.selected)
    .map(s => ({
      type: s.option || 'Shoutout',
      price: s.customItems?.[0]?.price || 'N/A',
    }));

  if (shoutoutItems.length) {
    services.shoutouts = shoutoutItems;
  }

  // Endorsements
  const endorsements = publicData?.endorsements || [];
  const endorsementItems = endorsements
    .filter(e => e?.selected)
    .map(e => ({
      type: `${e.platform || 'Platform'} - ${e.type || 'Type'}`,
      price: e.price || 'N/A',
    }));

  if (endorsementItems.length) {
    services.endorsements = endorsementItems;
  }

  // Appearances
  const appearances = publicData?.appearances || [];
  const appearanceItems = appearances
    .filter(a => a?.selected)
    .map(a => ({
      type: a.title || 'Appearance',
      price: a.price || 'N/A',
    }));

  if (appearanceItems.length) {
    services.appearances = appearanceItems;
  }

  // Creative Media
  const creativeMedia = publicData?.creativeMedia || [];
  const creativeMediaItems = creativeMedia
    .filter(m => m?.selected)
    .map(m => ({
      type: m.title || 'Creative Media',
      price: m.price || 'N/A',
    }));

  if (creativeMediaItems.length) {
    services.creativeMedia = creativeMediaItems;
  }

  return services;
};

// Helper function to extract Auctions (placeholder)
export const extractAuctions = (publicData = {}) => {
  const { auctions = [] } = publicData;

  const filteredAuctions = auctions
    .filter(a => a?.selected)
    .map(a => ({
      type: a.title || 'Auction Item',
      price: a.price || 'N/A',
    }));

  return { auctions: filteredAuctions };
};

/**
 * Stores the active category in session storage.
 * @param {string} category - The category to store.
 */
export const saveActiveCategoryToSession = category => {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('activeCategory', category);
  }
};

/**
 * Retrieves the active category from session storage.
 * @returns {string|null} The stored category or null if not available or window is undefined.
 */
export const loadActiveCategoryFromSession = () => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('activeCategory');
  }
  return null;
};

/**
 * Removes the active category from session storage.
 */
export const removeActiveCategoryFromSession = () => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('activeCategory');
  }
};
